import { adherencePercent } from '../lib/format.js';
import type { DayGlance, DayLineKind, SharedView as SharedPayload, WeekDayState } from '@adhd/shared';
import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { DayStrip } from '../components/DayStrip.js';
import { RecordSheet, type SheetRecord } from '../components/RecordSheet.js';
import { FALLBACK } from '../components/DayLifeEvent.js';
import { api, ApiError } from '../lib/api.js';
import { ADDON_CONTENTS, DAY_LINE_LABEL, DOCUMENTS, documentForRole, LEVEL_CONTENTS, levelAtLeast, TREND_COLUMN_GRANT, weekDayRow } from '@adhd/shared';
import { whenWritten, writtenLate } from '../lib/mood.js';
import { MOOD_WORDS, moodLabel } from '@adhd/shared';
import { DayEndPlate, ScalePointsPlate } from '../components/Plates.js';
import { SharedDocument } from './Documents.js';

/**
 * The page a specialist opens from a share link.
 *
 * It is the only screen in the app rendered for someone who is not the owner
 * and has no account, and three things follow from that.
 *
 * It renders fully logged out: no session, no navigation, nothing that reads a
 * user object. It is read-only, and it links nowhere into the app — every such
 * link is a sign-in wall for a visitor with nothing to sign in with, which is a
 * dead end that reads as a bug rather than as a boundary.
 *
 * And the payload is levelled. `data` carries only what the link's level and
 * add-ons grant, so every section below is written against a key that may
 * simply not be there. A section is shown whenever its key exists, empty or not: one that
 * disappeared when it had no rows would be indistinguishable from one that was
 * never shared, and "they logged nothing" and "you were not given this" are
 * very different things to tell a clinician.
 */

/** What came back instead of a view. Status 0 means the request never landed. */
interface Failure {
  status: number;
  message: string;
}

export function SharedView({ full = false }: { full?: boolean } = {}): JSX.Element {
  const { token } = useParams<{ token: string }>();
  const [view, setView] = useState<SharedPayload | null>(null);
  const [failure, setFailure] = useState<Failure | null>(null);

  useEffect(() => {
    setView(null);
    setFailure(null);
    if (!token) {
      setFailure({ status: 404, message: 'That link is not valid.' });
      return;
    }
    api
      .sharedView(token)
      .then(setView)
      .catch((e: unknown) =>
        setFailure(
          e instanceof ApiError
            ? { status: e.status, message: e.message }
            : { status: 0, message: "Can't reach the server right now. Check your connection." },
        ),
      );
  }, [token]);

  if (failure) return <Closed failure={failure} />;

  if (!view) {
    return (
      <Page>
        <div style={{ display: 'grid', placeItems: 'center', minHeight: '50dvh' }}>
          <div className="spinner" aria-label="Loading" />
        </div>
      </Page>
    );
  }

  /*
   * THE DOCUMENT FIRST (the fixes pass, 13 Oct 2026). A therapist or a
   * partner opens the document their role gets — the brief, the week — and
   * the sixteen-section page is one tap under it, at /shared/:token/page.
   * A friend, or a link with no role, opens the page. Same payload either
   * way; the document is a shape, not a grant.
   */
  const doc = documentForRole(view.role);
  if (doc && !full && token) {
    return (
      <Page>
        <SharedDocument view={view} kind={doc} pageHref={`/shared/${token}/page`} />
      </Page>
    );
  }
  return (
    <Page>
      <SharedPage view={view} token={token} docHref={doc && token ? `/shared/${token}/${doc}` : undefined} />
    </Page>
  );
}

/**
 * The page itself, for whoever holds the link and for the owner's "See what
 * they see" (SharePreview), which renders this same component inside his own
 * shell — so the two cannot show different things.
 */
export function SharedPage({ view, token, docHref }: { view: SharedPayload; token?: string; docHref?: string }): JSX.Element {
  /*
   * THE ONE THING THIS PAGE CAN DO BESIDES READ — release 5 of 2.0. One tap,
   * one row, no text, no reply. Offered only when the owner left it on for
   * this link, and only on the real page: the owner's preview passes no
   * token, so the button is not there to press by mistake.
   */
  const [waved, setWaved] = useState<'no' | 'sending' | 'yes' | 'failed'>('no');
  /*
   * Every hook here, above anything conditional, and that placement is the
   * point. These once sat next to the lists they govern, below two early
   * returns, so React saw four hooks on the first render and six on the next —
   * and the share page fell into its error boundary: a blank wall for someone
   * who was handed a link to a mental health record.
   *
   * The folds: the recent items are what a person opens this for, and a page
   * nobody reaches the end of has not really shared its later half at all.
   */
  const [journalAll, setJournalAll] = useState(false);
  const [recapAll, setRecapAll] = useState(false);
  /*
   * Which day in the strip is open, or null for none. Starts closed: opening
   * one by default would present a day the reader did not choose as though it
   * were the one worth looking at.
   */
  const [pickedDay, setPickedDay] = useState<string | null>(null);

  const { ownerDisplayName, ownerTimezone, label, generatedAt, expiresAt, data } = view;
  // An older server sends no level; the page then shows no level line and no
  // day labels, rather than guessing.
  const levelEntry = typeof view.level === 'string' ? LEVEL_CONTENTS[view.level] : undefined;
  const addonNames = (Array.isArray(view.addons) ? view.addons : [])
    .filter((a) => a in ADDON_CONTENTS)
    .map((a) => ADDON_CONTENTS[a].name);
  /*
   * Which kind of line each day carries, said on the day, only where a day can
   * carry either: What my days held and above. The display name, never a
   * guessed pronoun (DAY_LINE_LABEL).
   */
  const lineLabel =
    levelEntry && levelAtLeast(view.level, 'held')
      ? (kind: DayLineKind): string => DAY_LINE_LABEL[kind](ownerDisplayName)
      : undefined;
  // `data` is Record<string, unknown> on the wire and this is the one read that
  // was not narrowed, so an older server answering this link with a different
  // shape crashed the page rather than degrading. And `Object.hasOwn` is
  // ES2022: Vite transpiles syntax but never built-in methods, and this is the
  // single page in the app that renders on a device the owner does not control
  // — a clinical browser or an older iPad would have thrown outright.
  const has = (key: string): boolean =>
    typeof data === 'object' && data !== null && Object.prototype.hasOwnProperty.call(data, key);

  // The owner's calendar day, from the instant the payload was built. Every
  // 'Today'/'Yesterday' on this page has to be theirs — the reader is somewhere
  // else by definition.
  const ownerToday = dayIn(generatedAt, ownerTimezone);

  /*
   * Day by day HIDES what this link does not hold, rather than showing it as
   * "—". The server nulls those columns (TREND_COLUMN_GRANT, the map this
   * reads), and a null rendered as a dash is indistinguishable from "left
   * blank" — which is what the Check-ins note on this very page says a dash
   * means. An older server that sends no grant list keeps every column.
   */
  const granted = Array.isArray(view.grants) ? new Set<string>(view.grants) : null;
  const dayByDayHeads = ['Day', 'Mood', 'Focus', 'Check-ins', 'Records', 'Journal', 'Help now', 'Doses taken'];
  const dayByDaySource: (string | null)[] = [null, 'mean_mood', 'mean_focus', 'checkins', 'thought_records', 'voice_logs', 'sos_events', 'meds_taken'];
  const keepColumn = dayByDaySource.map((c) => {
    if (c === null || granted === null) return true;
    const grant = TREND_COLUMN_GRANT[c];
    return grant !== undefined && granted.has(grant);
  });
  const hiddenColumns = dayByDayHeads.filter((_, i) => !keepColumn[i]);
  // With no column at all the server sends no rows — and "Nothing logged" said
  // that about a person who may have logged every day.
  const noDayByDayColumn = granted !== null && keepColumn.every((keep, i) => i === 0 || !keep);
  const leftOut = (n: number): string =>
    `This link's level and add-ons leave ${n === 1 ? 'it' : 'them'} out — not missing from the record.`;

  const thoughtRecords = asRows(data.thoughtRecords).map(toSheetRecord);
  /* Shaped by the server (sharing.ts, the `homework` grant): one entry per
     homework, each carrying only the days that had something on them. */
  const homework = asRows(data.homework).map((h) => ({
    title: String(h.title ?? ''),
    /* The span it runs for. The server has always sent these (sharing.ts:1088)
       and this mapping dropped them on arrival, so a reader was shown a
       question and the days answered with no idea whether it was still being
       asked, or when it ends. */
    startedOn: str(h, 'startedOn'),
    endsOn: str(h, 'endsOn'),
    answers: asRows(h.answers).map((a) => ({
      day: String(a.day ?? ''),
      said: asRows(a.said).map((l) => ({ label: String(l.label ?? ''), text: String(l.text ?? '') })),
    })),
  }));
  const mood = asRows(data.mood);
  const trends = asRows(data.trends);
  const medications = asRows(data.medications);
  const adherence = asRows(data.adherence);
  const sosEvents = asRows(data.sosEvents);
  const transcripts = asRows(data.voiceTranscripts);
  const reports = asRows(data.reports).map(toReport);
  // Which weekly this link gets, as the server decided it (weeklyKindFor), so
  // the note describes this link's cards rather than guessing from them.
  const weeklyKind = data.weeklyKind === 'seemed' || data.weeklyKind === 'held' || data.weeklyKind === 'own_words'
    ? data.weeklyKind
    : null;
  const withEvents = granted?.has('lifeEvents') ?? false;
  const journalLimit = journalAll ? transcripts.length : 4;
  // One. A recap is several hundred words; two of them is most of a screen
  // each and the current week is the one being asked about.
  const recapLimit = recapAll ? reports.length : 1;
  const weeklyTitle =
    weeklyKind === 'seemed' ? 'How each week seemed' : weeklyKind === 'held' ? 'What each week held' : 'Weekly recaps';
  const weeklyNote =
    weeklyKind === 'seemed'
      ? `Written by the app from each day's state and the first sentence of the daily lines this link showed${
          withEvents ? ', and whether something significant was going on, never what' : ''
        }. It never sees ${ownerDisplayName}'s entries.${
          reports.some((r) => r.stats.length > 0) ? ' The figures beside it are counted from the data.' : ''
        }`
      : weeklyKind === 'held'
        ? `Written by the app only from that week's daily lines as this link showed them${
            withEvents ? ', and whether something significant was going on, never what' : ''
          }. The figures beside it are counted from the data.`
        : `Each recap is the one ${ownerDisplayName} gets, written by the app from that week's check-ins with their notes, worksheets, journal summaries and Help now moments. The counts underneath are computed from the data rather than written.`;

  return (
    <>
      <header className="page-header">
        <h1>{ownerDisplayName}'s record</h1>
        <p>
          Shared with you as “{label}”. Put together {formatStamp(generatedAt, ownerTimezone)}. It does not
          update on its own — reload the page to pick up anything written since.
        </p>
        {levelEntry && <p className="faint">{[levelEntry.name, ...addonNames].join(' · ')}</p>}
      </header>

      {/* Above the material rather than under it. Someone about to read another
          person's mental health record should know what this page is, and what
          the person who wrote it can still do about it, before they start. */}
      <div className="banner stack-sm stack">
        <span>
          <strong>This is a read-only view.</strong> Nothing here can be changed from this page, and
          it holds only the parts {ownerDisplayName} chose to include — not their whole record.
        </span>
        <span>
          {expiresAt === null
            ? 'No end date was set on this link.'
            : `The link stops working on ${formatStamp(expiresAt)}.`}{' '}
          {ownerDisplayName} can revoke it at any time, and can see when it was opened.
        </span>
        {/*
          IN THE BANNER, NOT A FOOTER. The questions this answers — why a day is
          blank, why a sentence says how somebody IS rather than what happened,
          what is deliberately missing — are ones the reader has BEFORE they
          start, and are exactly the ones that get misread as bad news. A link
          at the bottom arrives after the damage.

          A plain <a> because /help is a static page the server hands over, not
          a route in this router; and /help#shared lands on the half written for
          the person holding a link rather than at the top of the owner's
          manual. It carries nothing about this link or this person.

          `linkish` because there is no global rule for a bare <a> in this app —
          almost every link is a <Link> or a button — so an unclassed anchor
          renders in default browser blue, which on this page would be the one
          cold primary colour in a warm palette, on the screen a clinician sees.
        */}
        <span>
          New to this?{' '}
          <a className="linkish" href="/help#shared" target="_blank" rel="noopener noreferrer">
            What this page means
          </a>{' '}
          — a short explanation of how it is put together, and what it leaves out.
        </span>
      </div>

      {/* The document this link's role gets (release 5b): the same page in
          reading order, on one printable sheet. A shape, not a grant — it is
          rendered from this very payload, so it cannot show more. */}
      {docHref && documentForRole(view.role) && (
        <p className="doc-offer">
          <Link className="linkish" to={docHref}>
            {DOCUMENTS[documentForRole(view.role)!].name}, as one page to read or print ›
          </Link>
        </p>
      )}

      {Object.keys(data).length === 0 && (
        <Nothing>
          <p>This link was made without any categories on it, so there is nothing to show.</p>
        </Nothing>
      )}

      {/* First on the page, deliberately. Whoever opened this link wants to know
          how the person is; everything below is the working-out. */}
      {/* Before the day, because it explains it. Someone opening this to see how
          a person is doing needs to know a bereavement is running before they
          read a flat Tuesday as an ordinary flat Tuesday. */}
      {has('lifeEvents') && asRows(data.lifeEvents).length > 0 && (
        <Section title="Threads">
          <div className="stack-sm stack">
            <p className="muted" style={{ margin: 0 }}>
              A thread is a life event, big or small — something shaping these days — named by {ownerDisplayName} or marked as “{FALLBACK}”, with how long it has run.
            </p>
            {asRows(data.lifeEvents).map((e, i) => (
              <div className="life-event" key={`${str(e, 'label') ?? ''}-${i}`}>
                {/* Unnamed is the ordinary case, not a gap. Marking something
                    takes two taps and no keyboard so it can be done on the day;
                    naming is a later step that may never come. "Something big"
                    is not a category the app assigned — it is the absence of a
                    name, stated honestly, and it is most of what a partner or a
                    GP actually needed. Imported rather than retyped: this was
                    one of three literals of the same constant, and they had
                    already drifted apart. */}
                {/* The same mark and the same words the owner sees.
                    The bookmark was deliberately left off here at first, on the
                    grounds that it is shorthand between the app and its owner.
                    That was the wrong call: a reader meets this row cold, and
                    the tag is what says "this is a category of thing the app
                    tracks" rather than a bare sentence somebody typed. */}
                <p className="life-event-tag">
                  <span aria-hidden="true">🔖</span> Thread
                </p>
                <strong>{str(e, 'label') || FALLBACK}</strong>
                <span className="faint">
                  {/* THE DAY COUNTER STAYS. Kept on purpose, re-confirmed by the
                      owner on 7 Sep 2026, and this note lives here rather than
                      only in DayLifeEvent.tsx because reasoning in a different
                      file from the code is how the wording in this feature
                      drifted in the first place.

                      The app forbids a counter on HIS screens — "day 12 turns a
                      bereavement into a tally" — and allows it here on the
                      grounds that a reader taking in a duration is doing a
                      different thing from a person being handed a running total
                      of their own life.

                      The standing objection, so it need not be rediscovered: now
                      that the words beside it are neutral, this number is the
                      only claim left on the row, and a clinical reader takes a
                      rising count as escalation. Put to him with a
                      recommendation to drop it; he chose to keep it. Do not
                      remove it without asking him again. */}
                  Since {dayCell(str(e, 'startedOn'))}
                  {(() => {
                    const d = num(e, 'daysRunning');
                    return d !== null && d > 0 ? ` · day ${d + 1}` : '';
                  })()}
                  {str(e, 'endedOn') ? ` · ended ${dayCell(str(e, 'endedOn'))}` : ''}
                </span>
              </div>
            ))}
          </div>
        </Section>
      )}

      {/* PLATE ONE, before anything counted. The shape of the window is the
          first thing a clinician with ninety seconds should meet, and it is
          the one drawing that answers "has this been going on" without asking
          anybody to read a table. */}
      {has('dailyRecap') && (
        <Section title="The shape of it">
          {/* The whole window, not the week. `dailyRecap` is seven days — it
              is the strip below — and a chart of seven days cannot answer the
              question a clinician actually has, which is whether this has been
              going on. `trends` carries every day the link discloses, up to
              120, oldest first once reversed. */}
          <DayEndPlate
            days={[...trends].reverse().map((t) => ({
              day: str(t, 'day') ?? '',
              lastMood: num(t, 'last_mood'),
              lowestMood: null,
              highestMood: null,
            }))}
          />
          {/* PLATE TWO sits under it, because the distribution is the same
              ratings counted a second way and reads as a footnote to the
              series rather than as a second subject. */}
          {mood.length > 0 && (
            <ScalePointsPlate
              counts={mood.reduce<Record<number, number>>((acc, r) => {
                const m = num(r, 'mood');
                if (m !== null) acc[m] = (acc[m] ?? 0) + 1;
                return acc;
              }, {})}
            />
          )}
        </Section>
      )}

      {/* The key, before the first figure that needs it. Every mood number on
          this page used to arrive with nothing attached: no word, no unit, and
          a range sentence further down that named the ends and no point in
          between. */}
      {(has('dailyRecap') || has('mood') || has('dailyStatus')) && (
        <MoodLegend rows={mood} ownerName={ownerDisplayName} />
      )}

      {/* What helps — the owner's own sentence about what helps when it's hard,
          shown only because they chose to. Never written by the app. */}
      {/* A line from the owner for this person (14 Oct 2026): his words, for
          this reader, shown while he leaves it on. Above what helps, because
          it is addressed to them. */}
      {view.note && (
        <section className="card stack-sm stack what-helps">
          <h2 className="section-head">From {ownerDisplayName}</h2>
          <p className="reading" style={{ margin: 0, whiteSpace: 'pre-wrap' }}>{view.note}</p>
        </section>
      )}
      {view.whatHelps && (
        <section className="card stack-sm stack what-helps">
          <h2 className="section-head">What helps</h2>
          <p className="reading" style={{ margin: 0 }}>{view.whatHelps}</p>
        </section>
      )}
      {token && view.wavesOn && (
        <section className="stack-sm stack">
          <button
            type="button"
            className="btn btn-primary btn-lg btn-block"
            disabled={waved !== 'no'}
            onClick={() => {
              setWaved('sending');
              void api.wave(token).then(() => setWaved('yes')).catch(() => setWaved('failed'));
            }}
          >
            {waved === 'yes' ? 'They will see that' : waved === 'sending' ? '…' : waved === 'failed' ? 'That did not go through' : "I'm thinking of you"}
          </button>
          <p className="faint" style={{ margin: 0 }}>
            One tap. {view.ownerDisplayName} sees a line with your name on it. No message, no reply, and nothing else you read
            or tap here is passed on.
          </p>
        </section>
      )}
      {has('dailyStatus') && (
        <DailyStatus
          row={isRow(data.dailyStatus) ? data.dailyStatus : null}
          lineLabel={lineLabel}
        />
      )}

      {/* The week behind today. The first question anyone worried about someone
          actually has is not "how is he right now" but "has this been going
          on", and a single day cannot answer it. */}
      {has('dailyRecap') && (
        <Section title="The week">
          <WeekBehind
            days={asRows(data.dailyRecap) as unknown as DayGlance[]}
            picked={pickedDay}
            onPick={setPickedDay}
            today={ownerToday}
            /* Taken from the block above rather than computed here. Today has
               to mean the owner's today — a reader in another timezone asking
               their own device would mark the wrong card, on the one screen in
               the app whose reader is somewhere else by definition. */
            alreadyShown={isRow(data.dailyStatus) ? str(data.dailyStatus, 'day') : null}
            lineLabel={lineLabel}
          />
        </Section>
      )}

      {has('thoughtRecords') && (
        <Section title="Thought records">
          {thoughtRecords.length === 0 ? (
            <Nothing>
              <p>No thought records in the period that was shared.</p>
            </Nothing>
          ) : (
            /* An index that opens, not a stack of worksheets.
               Each record is a full A4-proportioned sheet, and shared in bulk
               they became a wall — the owner's word was "overwhelming". Twenty of
               them in a column is not twenty times as useful as one; it is a
               document nobody scrolls to the end of, which in a clinical setting
               means the later records may as well not have been shared.

               So the default is a line per record carrying the three things
               worth scanning for: when, the thought itself, and whether belief
               moved. The whole sheet is one tap away and unchanged — this hides
               nothing, it just stops insisting everything is read at once. */
            <RecordIndex records={thoughtRecords} timezone={ownerTimezone} />
          )}
        </Section>
      )}

      {has('homework') && (
        <Section
          title="Therapy homework"
          note="What they were asked to keep track of, and what they wrote, in their own words. Days they did not write are not listed, and there is no count of how many there were."
        >
          {homework.length === 0 ? (
            <Nothing>
              <p>Nothing written for homework in the period that was shared.</p>
            </Nothing>
          ) : (
            <div className="stack-sm stack">
              {homework.map((h, i) => (
                <div key={i} className="stack-sm stack">
                  <h3 className="reading" style={{ margin: 0 }}>
                    {h.title}
                  </h3>
                  {/* The span, never a count of the days inside it. How many
                      days he answered on is his business and a fraction of
                      them is a grade — see sharing.ts, "NO COUNTS". */}
                  {h.startedOn !== null && h.endsOn !== null && (
                    <span className="faint">
                      Set for {dayCell(h.startedOn)} to {dayCell(h.endsOn)}
                    </span>
                  )}
                  {h.answers.map((a, j) => (
                    <article key={j} className="card stack-sm stack">
                      <span className="faint">{longDay(a.day)}</span>
                      {a.said.map((line, k) => (
                        <div key={k}>
                          {h.answers.some((x) => x.said.length > 1) && (
                            <span className="faint">{line.label}</span>
                          )}
                          <p className="reading" style={{ margin: 0, whiteSpace: 'pre-wrap' }}>
                            {line.text}
                          </p>
                        </div>
                      ))}
                    </article>
                  ))}
                </div>
              ))}
            </div>
          )}
        </Section>
      )}

      {has('mood') && (
        <Section
          title="Check-ins"
          /* The scale is stated ONCE, by the legend above, which renders
             MOOD_WORDS. This note used to restate it in prose — "Mood runs
             from −5 to +5" — which made it the third statement of a rule whose
             first statement is the code, and a restatement is not checked
             against the rule. What is left here is what the legend does not
             cover. */
          note="A dash means it was left blank — every field except mood is optional, by design. Anxiety was first asked on 10 September 2026, so earlier check-ins have none. The other seven run 1 to 5, and their direction differs: high Focus and Energy are good, high Overwhelm, Restlessness, Irritability, Rejection sting and Anxiety are not."
        >
          {mood.length === 0 ? (
            <Nothing>
              <p>No check-ins in the period that was shared.</p>
            </Nothing>
          ) : (
            // Every slider the check-in screen asks, in its order and under its
            // labels (CheckIn.tsx). It was four of them, for no reason anybody
            // wrote down; anxiety joined the screen on 10 Sep.
            <Table
              columns={[
                'When', 'Mood', 'Focus', 'Energy', 'Overwhelm', 'Restlessness', 'Irritability', 'Rejection sting',
                'Anxiety',
              ]}
              rows={mood.map((row, i) => ({
                key: `${str(row, 'recorded_at') ?? ''}-${i}`,
                cells: [
                  whenCell(str(row, 'day'), str(row, 'recorded_at'), ownerTimezone),
                  signed(num(row, 'mood')),
                  plain(num(row, 'focus')),
                  plain(num(row, 'energy')),
                  plain(num(row, 'overwhelm')),
                  plain(num(row, 'restlessness')),
                  plain(num(row, 'irritability')),
                  plain(num(row, 'rejection_sensitivity')),
                  plain(num(row, 'anxiety')),
                ],
              }))}
            />
          )}
        </Section>
      )}

      {has('sosEvents') && (
        <Section
          title="Moments they asked for help"
          note="Each row is a time they opened Help now and worked through something. Distress is their own rating out of 100, before and after — and either may be blank, because rating it is optional."
        >
          {sosEvents.length === 0 ? (
            <Nothing>
              <p>None in the period that was shared.</p>
            </Nothing>
          ) : (
            <Table
              columns={['When', 'Distress', 'What they did']}
              rows={sosEvents.map((row, i) => ({
                key: `${str(row, 'started_at') ?? ''}-${i}`,
                cells: [
                  whenCell(str(row, 'day'), str(row, 'started_at'), ownerTimezone),
                  shift(num(row, 'distress_before'), num(row, 'distress_after')),
                  sosPathName(str(row, 'path_taken')),
                ],
              }))}
            />
          )}
        </Section>
      )}

      {has('medications') && (
        <Section title="Medication">
          {medications.length === 0 ? (
            <Nothing>
              <p>Nothing recorded.</p>
            </Nothing>
          ) : (
            <Table
              columns={['Medication', 'Dose', 'Since', 'Status']}
              rows={medications.map((row, i) => ({
                key: `${str(row, 'name') ?? ''}-${i}`,
                cells: [
                  str(row, 'name') ?? DASH,
                  doseCell(num(row, 'dose_amount'), str(row, 'dose_unit')),
                  dayCell(str(row, 'started_on')),
                  num(row, 'is_active') === 1 ? 'Taking' : 'Stopped',
                ],
              }))}
            />
          )}
        </Section>
      )}

      {has('adherence') && (
        // The caveat is spelled out because the app genuinely cannot see past
        // the tap, and a clinician reading an adherence table will otherwise
        // reasonably assume it means doses actually taken.
        <Section
          title="Doses taken"
          // The caveat now has to carry the default as well as the old one. A
          // clinician reading "2 of 2" is entitled to know that a dose nobody
          // touched is counted as taken — otherwise the column reads as a record
          // of confirmations, and it is not one. The "assumed" column says how
          // much of each row that applies to.
          note="A dose whose time had passed is counted as taken unless it was marked missed or skipped, so this reflects the app's default rather than a confirmation. The assumed column shows how many of each day's doses were never marked either way. Missed and skipped are what they marked — skipped means on purpose. None of it is a record of what was actually swallowed."
        >
          {adherence.length === 0 ? (
            <Nothing>
              <p>No days with a scheduled dose in the period that was shared.</p>
            </Nothing>
          ) : (
            <>
              {/* The headline before the day-by-day. A clinician wants the rate
                  first and the workings underneath; reading thirty rows to add
                  them up is work the page should have done. The period is stated
                  in days actually covered rather than "30 days", because the
                  table only ever contains days a dose was scheduled — saying
                  30 when it holds 3 would be the page overstating itself. */}
              <AdherenceHeadline
                rows={adherence}
                ownerToday={dayIn(generatedAt, ownerTimezone)}
              />
              <Table
              columns={['Day', 'Taken', 'Assumed', 'Missed', 'Skipped', 'Scheduled']}
              rows={adherence.map((row, i) => ({
                key: `${str(row, 'day') ?? ''}-${i}`,
                cells: [
                  dayCell(str(row, 'day')),
                  plain(num(row, 'meds_taken')),
                  plain(num(row, 'meds_assumed')),
                  plain(num(row, 'meds_missed')),
                  plain(num(row, 'meds_skipped')),
                  plain(num(row, 'meds_scheduled')),
                ],
              }))}
              />
            </>
          )}
        </Section>
      )}

      {has('trends') && (
        <Section
          title="Day by day"
          // The doses column carries the same default as the table above, and
          // this section can be shared without that one — so the caveat has to
          // be repeated rather than assumed to have been read.
          note="One row per day, newest first. Mood and focus are the day's mean across that day's check-ins. A dose whose time had passed counts as taken unless it was marked missed or skipped, so that column reflects the app's default rather than a confirmation."
        >
          {trends.length === 0 ? (
            <Nothing>
              <p>
                {/* "Nothing logged" is only true when every column is here. */}
                {noDayByDayColumn
                  ? "This link's level does not include any of this table's columns, so there are no rows to show."
                  : hiddenColumns.length > 0
                    ? `Nothing in the columns this link includes, for the period that was shared. Not included on this link: ${hiddenColumns.join(', ')}. ${leftOut(hiddenColumns.length)}`
                    : 'Nothing logged in the period that was shared.'}
              </p>
            </Nothing>
          ) : (
            <>
              <Table
                columns={dayByDayHeads.filter((_, i) => keepColumn[i])}
                rows={trends.map((row, i) => ({
                  key: `${str(row, 'day') ?? ''}-${i}`,
                  cells: [
                    dayCell(str(row, 'day')),
                    signed(num(row, 'mean_mood'), 1),
                    plain(num(row, 'mean_focus'), 1),
                    plain(num(row, 'checkins')),
                    plain(num(row, 'thought_records')),
                    plain(num(row, 'voice_logs')),
                    plain(num(row, 'sos_events')),
                    ofCell(num(row, 'meds_taken'), num(row, 'meds_scheduled')),
                  ].filter((_, j) => keepColumn[j]),
                }))}
              />
              {hiddenColumns.length > 0 && (
                <p className="faint">
                  Not included on this link: {hiddenColumns.join(', ')}. {leftOut(hiddenColumns.length)}
                </p>
              )}
            </>
          )}
        </Section>
      )}

      {has('voiceTranscripts') && (
        <Section
          title="Journal"
          note="Short summaries only. The recordings themselves are never shared, and neither are the full transcripts."
        >
          {transcripts.length === 0 ? (
            <Nothing>
              <p>No journal entries in the period that was shared.</p>
            </Nothing>
          ) : (
            <div className="stack">
              {transcripts.slice(0, journalLimit).map((row, i) => {
                const summary = str(row, 'summary');
                return (
                  <article className="card stack-sm stack" key={`${str(row, 'day') ?? ''}-${i}`}>
                    <div className="row-between">
                      <strong>{str(row, 'title') ?? 'Journal entry'}</strong>
                      <span className="faint">{dayCell(str(row, 'day'))}</span>
                    </div>
                    {summary === null ? (
                      <p className="muted">This entry was never summarised.</p>
                    ) : (
                      <p className="reading">{summary}</p>
                    )}
                  </article>
                );
              })}
                {transcripts.length > journalLimit && (
                  <button
                    className="btn btn-ghost btn-block btn-compact"
                    onClick={() => setJournalAll(true)}
                  >
                    Show {transcripts.length - journalLimit} older{' '}
                    {transcripts.length - journalLimit === 1 ? 'entry' : 'entries'}
                  </button>
                )}
            </div>
          )}
        </Section>
      )}

      {has('reports') && (
        <Section title={weeklyTitle} note={weeklyNote}>
          {reports.length === 0 ? (
            <Nothing>
              <p>{weeklyKind === 'own_words' ? 'No weekly recaps have been generated.' : 'No finished weeks to show yet.'}</p>
            </Nothing>
          ) : (
            <div className="stack-lg stack">
              {reports.slice(0, recapLimit).map((report, i) => (
                <article className="card stack" key={`${report.weekStart}-${i}`}>
                  <h3>
                    {dayCell(report.weekStart)} – {dayCell(report.weekEnd)}
                  </h3>
                  {report.stats.length > 0 && <p className="faint">{report.stats.join(' · ')}</p>}
                  {/* The day readings' own three words, one per day, counted
                      rather than written — on the weeks this link was live for. */}
                  {report.days !== null && <p className="faint">{weekDayRow(report.days)}</p>}

                  {report.summary === null && report.days !== null ? (
                    <p className="muted">No written reading for this week.</p>
                  ) : report.summary === null && (weeklyKind === 'seemed' || weeklyKind === 'held') ? (
                    // A week reads only from the first full week at this level.
                    <p className="muted">No reading for this week: it began before this link reached its level.</p>
                  ) : report.summary === null ? (
                    <p className="muted">No recap for this week — none was written.</p>
                  ) : (
                    report.summary.map((paragraph, p) => (
                      <p className="reading" key={p}>
                        {paragraph}
                      </p>
                    ))
                  )}

                  {report.highlights.length > 0 && (
                    <div className="stack-sm stack">
                      {report.highlights.map((highlight, h) => (
                        <div key={h}>
                          <span className="faint">{dayCell(highlight.day)}</span>
                          <p>{highlight.text}</p>
                        </div>
                      ))}
                    </div>
                  )}

                  {report.patterns.length > 0 && (
                    <div className="chips">
                      {report.patterns.map((pattern) => (
                        // Not a button: there is nothing on this page to press,
                        // and a chip that looks tappable invites a reader to try
                        // editing their patient's record.
                        <span className="chip" key={pattern} style={{ cursor: 'default' }}>
                          {pattern}
                        </span>
                      ))}
                    </div>
                  )}
                </article>
              ))}
              {/*
                Outside the loop, and outside the highlights block it was nested
                in. It sat inside `report.highlights.length > 0`, per report —
                so it rendered once for every week that happened to have
                highlights, and not at all when the shown weeks had none. On a
                share link that is the difference between a clinician reaching a
                fortnight of recaps and reaching the two most recent, with
                nothing on screen to say the rest exist.
              */}
              {reports.length > recapLimit && (
                <button
                  className="btn btn-ghost btn-block btn-compact"
                  onClick={() => setRecapAll(true)}
                >
                  Show {reports.length - recapLimit} older{' '}
                  {weeklyKind === 'own_words'
                    ? reports.length - recapLimit === 1 ? 'recap' : 'recaps'
                    : reports.length - recapLimit === 1 ? 'week' : 'weeks'}
                </button>
              )}
            </div>
          )}
        </Section>
      )}
    </>
  );
}

/**
 * The page shell.
 *
 * `.app` reserves a band of bottom padding for the fixed navigation bar, which
 * this page deliberately does not render — left as is, that reservation is dead
 * space running off the end of a page nobody can navigate away from anyway.
 */
function Page({ children }: { children: React.ReactNode }): JSX.Element {
  return (
    <main className="app stack-lg stack" style={{ paddingBottom: 'var(--space-8)' }}>
      {children}
    </main>
  );
}

/**
 * What is shown when the link does not open.
 *
 * The server answers expired, revoked and never-existed with one identical
 * message on purpose: three distinct replies would confirm which tokens once
 * existed to anyone guessing at them. So this shows exactly what it was given
 * and adds nothing — copy here that guessed at which of the three it was would
 * leak precisely what the server refuses to.
 */
function Closed({ failure }: { failure: Failure }): JSX.Element {
  const needsAccount = failure.status === 403;
  const offline = failure.status === 0;

  return (
    <Page>
      <header className="page-header">
        <h1>
          {needsAccount
            ? 'This link needs you signed in'
            : offline
              ? "Can't reach the server"
              : "This link isn't working"}
        </h1>
        <p>{failure.message}</p>
      </header>

      {needsAccount && (
        <>
          <div className="banner banner-caution">
            Whoever shared this chose to require an account, so holding the link is not enough on
            its own. It opens only for the address they addressed it to — sign in as that person,
            then open this link again.
          </div>
          {/* The rest of this page links nowhere on purpose, but this is the one
              state that asks the reader to go and do something, and leaving them
              to work out that they should hand-edit the URL down to the site
              root made the boundary read as a broken page instead. */}
          <a className="btn btn-secondary btn-block" href="/">
            Go to the sign-in screen
          </a>
        </>
      )}

      {offline && (
        <button className="btn btn-secondary btn-block" onClick={() => window.location.reload()}>
          Try again
        </button>
      )}
    </Page>
  );
}

function Section({
  title,
  note,
  children,
}: {
  title: string;
  note?: string;
  children: React.ReactNode;
}): JSX.Element {
  return (
    <section className="stack">
      <h2>{title}</h2>
      {note !== undefined && <p className="muted">{note}</p>}
      {children}
    </section>
  );
}

function Nothing({ children }: { children: React.ReactNode }): JSX.Element {
  return <div className="card empty">{children}</div>;
}

/**
 * A small table of already-formatted cells.
 *
 * Inline styles rather than a class: this is the only page in the app that
 * shows tabular data, and a `.table` in the global stylesheet would be a shared
 * thing with exactly one caller. The scroll container is not optional — eight
 * columns do not fit a phone, and without it the whole page scrolls sideways
 * and the header slides off with it.
 */
/**
 * A table that stops rather than running.
 *
 * Sharing everything produced a page nobody reaches the end of: on a phone the
 * check-ins alone ran to fifteen hundred pixels, the Help now moments the same
 * again, before the records had even started. Length is not thoroughness — past
 * a screen or two it is the opposite, because the reader stops.
 *
 * The most recent rows are the ones a person opens this to see, so those are
 * kept and the rest wait behind a count. Nothing is withheld: the button says
 * exactly how many are underneath, which a scrollbar never did.
 */
function Table({
  columns,
  rows,
  limit = 12,
}: {
  columns: string[];
  rows: { key: string; cells: string[] }[];
  limit?: number;
}): JSX.Element {
  const [all, setAll] = useState(false);
  const hidden = Math.max(0, rows.length - limit);
  const shown = all || hidden === 0 ? rows : rows.slice(0, limit);

  return (
    <>
    <div className="card" style={{ overflowX: 'auto', padding: 'var(--space-2)' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 'var(--text-sm)' }}>
        <thead>
          <tr>
            {columns.map((column) => (
              <th key={column} style={HEAD_CELL}>
                {column}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {shown.map((row) => (
            <tr key={row.key}>
              {row.cells.map((cell, i) => (
                <td key={i} style={BODY_CELL}>
                  {cell}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
    {hidden > 0 && !all && (
      <button className="btn btn-ghost btn-block btn-compact" onClick={() => setAll(true)}>
        Show {hidden} more {hidden === 1 ? 'row' : 'rows'}
      </button>
    )}
    </>
  );
}

const HEAD_CELL: React.CSSProperties = {
  textAlign: 'left',
  fontWeight: 650,
  color: 'var(--text-muted)',
  padding: 'var(--space-2)',
  borderBottom: '1px solid var(--border-strong)',
  whiteSpace: 'nowrap',
};

const BODY_CELL: React.CSSProperties = {
  padding: 'var(--space-2)',
  borderBottom: '1px solid var(--border)',
  whiteSpace: 'nowrap',
  // Proportional digits make a column of numbers jog left and right down the
  // page, which is exactly the column someone is here to scan.
  fontVariantNumeric: 'tabular-nums',
};

/* --- Narrowing ----------------------------------------------------------
 *
 * `data` is Record<string, unknown> in the contract, and it arrives from the
 * network as parsed JSON. Casting it to the shape this page wants would be a
 * promise the type system cannot keep — a server that changed a column name, or
 * an older deployment behind the same link, would produce "undefined" printed
 * into a therapist's copy of someone's record rather than a missing row. So
 * every value is checked, and anything unrecognisable is dropped instead of
 * rendered.
 */

/**
 * What the seven words mean, and how often each has been his answer.
 *
 * WHY THIS EXISTS. `moodLabel` has banded this scale into seven words since
 * August and the check-in screen prints the word under his thumb as he drags.
 * None of it reached a recipient: SharedView imported two helpers from that
 * exact file and left `moodLabel` behind, so a clinician was shown a signed
 * integer, a colour computed from a word she had never been given, and one
 * sentence naming the two ends. This is the key.
 *
 * IT RENDERS `MOOD_WORDS` RATHER THAN RESTATING IT. The bounds come from the
 * same table `moodLabel` is built on, so the legend cannot drift from the
 * function — and the note on the Check-ins table below no longer describes the
 * scale in prose, because a restatement of a rule is a second implementation
 * of it and is never checked against the first.
 *
 * THE COUNTS ARE A SAMPLE SIZE, NOT A SCORE. "17" is how many of his check-ins
 * landed on that word. There is no total, no percentage and no bar: a bar would
 * be seven equal ones over bands that are not equally wide, which is precisely
 * how a flat distribution came to be read as two groups. They appear only where
 * the per-check-in rows were shared; at the narrowest rung the words and their
 * bounds carry the legend alone.
 *
 * A WORD WITH A COUNT OF ZERO STAYS. He has never chosen "Great" and has never
 * gone below −4. Dropping the untouched ends would hide the shape of what he
 * has actually used, which is the one thing a reader most needs in order not to
 * read −4 as a near-miss.
 */
function MoodLegend({
  rows,
  ownerName,
}: {
  rows: Record<string, unknown>[];
  ownerName: string;
}): JSX.Element {
  const counts = new Map<string, number>();
  let counted = 0;
  for (const r of rows) {
    const m = num(r, 'mood');
    if (m === null) continue;
    counts.set(moodLabel(m), (counts.get(moodLabel(m)) ?? 0) + 1);
    counted += 1;
  }

  /* His floor and his ceiling, from what he has actually recorded — stated so
     that the lowest figure on the page does not read as one notch off the worst
     available when it is in fact the worst he has recorded. */
  const used = rows.map((r) => num(r, 'mood')).filter((m): m is number => m !== null);
  const lo = used.length > 0 ? Math.min(...used) : null;
  const hi = used.length > 0 ? Math.max(...used) : null;

  return (
    <Section title="How to read the rating">
      <p className="muted" style={{ marginTop: 0 }}>
        {ownerName} moves one slider when he checks in. It runs from Awful to Great, and the app
        shows him the word as he moves it. These are the seven words and the ratings each one
        covers.
      </p>
      <dl className="sv-legend">
        {MOOD_WORDS.map((b) => {
          const n = counts.get(b.word);
          return (
            <div key={b.word}>
              <dt>{b.word}</dt>
              <dd>
                <span className="sv-legend-bounds">
                  {b.lo === b.hi ? signed(b.lo) : `${signed(b.lo)} to ${signed(b.hi)}`}
                </span>
                {counted > 0 && (
                  <span className="sv-legend-count">
                    {n === undefined || n === 0 ? 'not used' : `${n} check-in${n === 1 ? '' : 's'}`}
                  </span>
                )}
              </dd>
            </div>
          );
        })}
      </dl>
      {lo !== null && hi !== null && (
        <p className="faint" style={{ marginBottom: 0 }}>
          The scale runs {signed(-5)} to {signed(5)}. In what has been shared here he has used{' '}
          {signed(lo)} to {signed(hi)}, so read {signed(lo)} as the lowest he has recorded rather
          than one notch off the worst available.
        </p>
      )}
    </Section>
  );
}

export function isRow(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

export function asRows(value: unknown): Record<string, unknown>[] {
  return Array.isArray(value) ? value.filter(isRow) : [];
}

export function str(row: Record<string, unknown>, key: string): string | null {
  const value = row[key];
  return typeof value === 'string' ? value : null;
}

export function num(row: Record<string, unknown>, key: string): number | null {
  const value = row[key];
  return typeof value === 'number' && Number.isFinite(value) ? value : null;
}

export function strings(value: unknown): string[] {
  if (!Array.isArray(value)) return [];
  return value.filter((item): item is string => typeof item === 'string');
}

/**
 * A shared record, reduced to the fields the sheet prints.
 *
 * Nothing here drops a record, and missing text becomes an empty string:
 * RecordSheet already omits a section it has nothing for, a worksheet whose
 * situation failed to decrypt still has a thought and evidence worth reading,
 * and a record quietly missing from a clinical page is the one failure the
 * reader has no way to notice. The day stands in for the timestamp for the same
 * reason — both are on the payload, and the sheet heads itself with a date.
 */
export function toSheetRecord(row: Record<string, unknown>): SheetRecord {
  return {
    // `day` first, as the comment above says: it is already the owner's
    // calendar day, computed server-side in their timezone. `occurredAt` is an
    // instant, and formatting it in the reader's zone dates a record written
    // just after midnight to the day before.
    occurredAt: str(row, 'day') ?? str(row, 'occurredAt') ?? '',
    format: str(row, 'format') ?? 'standard',
    situation: str(row, 'situation') ?? '',
    automaticThought: str(row, 'automaticThought') ?? '',
    beliefBefore: num(row, 'beliefBefore'),
    beliefAfter: num(row, 'beliefAfter'),
    medications: asRows(row.medications).map((m) => ({
      name: str(m, 'name') ?? '',
      doseAmount: num(m, 'doseAmount'),
      doseUnit: str(m, 'doseUnit') ?? '',
      status: str(m, 'status') ?? 'taken',
      at: str(m, 'at'),
      feelings: Array.isArray(m.feelings) ? (m.feelings as string[]) : [],
    })),
    emotions: asRows(row.emotions).flatMap((emotion) => {
      const name = str(emotion, 'emotion');
      const before = num(emotion, 'intensityBefore');
      return name !== null && before !== null
        ? [{ emotion: name, intensityBefore: before, intensityAfter: num(emotion, 'intensityAfter') }]
        : [];
    }),
    evidence: asRows(row.evidence).flatMap((entry) => {
      const side = str(entry, 'side');
      const text = str(entry, 'text');
      return (side === 'for' || side === 'against') && text !== null ? [{ side, text }] : [];
    }),
    distortions: strings(row.distortions),
    appDistortions: strings(row.appDistortions),
    response: str(row, 'response'),
    isComplete: row.isComplete === true,
  };
}

export interface WeeklyRecap {
  weekStart: string;
  weekEnd: string;
  /** Paragraphs, or null when the week's recap was never written. */
  summary: string[] | null;
  /** The seven day states, on a How the week seemed or What the week held card, for live weeks. */
  days: WeekDayState[] | null;
  stats: string[];
  highlights: { day: string; text: string }[];
  patterns: string[];
}

export function toReport(row: Record<string, unknown>): WeeklyRecap {
  const stats: Record<string, unknown> = isRow(row.stats) ? row.stats : {};
  const meanMood = num(stats, 'meanMood');
  const bestDay = str(stats, 'bestDay');
  const hardestDay = str(stats, 'hardestDay');
  const adherence = num(stats, 'medAdherenceRate');
  const summary = str(row, 'summaryMd');

  return {
    weekStart: str(row, 'weekStart') ?? '',
    weekEnd: str(row, 'weekEnd') ?? '',
    summary: summary === null ? null : paragraphs(summary),
    days: Array.isArray(row.days)
      ? (row.days as unknown[]).flatMap((d) =>
          isRow(d) ? [{ day: str(d, 'day') ?? '', state: (str(d, 'state') as WeekDayState['state']) ?? null }] : [],
        )
      : null,
    stats: [
      countOf(num(stats, 'activeDays'), 'day logged', 'days logged'),
      countOf(num(stats, 'checkins'), 'check-in', 'check-ins'),
      countOf(num(stats, 'thoughtRecords'), 'thought record', 'thought records'),
      countOf(num(stats, 'voiceLogs'), 'journal entry', 'journal entries'),
      // "Help now" is the name on the button and the nav tab. SOS is what the
      // table is called. It was taken out of every line a person reads months
      // ago and survived here — on the one screen its owner never opens, so
      // the only reader who could have noticed was his clinician.
      countOf(num(stats, 'sosEvents'), 'Help now moment', 'Help now moments'),
      meanMood === null ? null : `mood ${moodLabel(meanMood).toLowerCase()} on average, ${signed(meanMood, 1)}`,
      /*
       * The week's highest and lowest ending, at last.
       *
       * `bestDay` and `hardestDay` have been computed in reports.ts:65-66,
       * granted under dayMoodFigures at the narrowest rung, serialised and sent
       * to the reader's browser all along — and drawn nowhere. Neither string
       * appeared anywhere in packages/web. Drawing them removes a discard
       * rather than disclosing anything new.
       *
       * Named as ENDINGS because that is what they are: the days are ranked on
       * where each one finished, which is the same figure the cards now show.
       * Not "best" and "worst" to a reader, which grades a person's week.
       */
      hardestDay === null ? null : `hardest ending ${dayCell(hardestDay)}`,
      bestDay === null || bestDay === hardestDay ? null : `easiest ending ${dayCell(bestDay)}`,
      // "Counted as", not "marked": the rate includes doses nobody marked, which
      // the Doses taken section on this same page says in so many words.
      adherence === null ? null : `${Math.round(adherence * 100)}% of scheduled doses counted as taken`,
    ].filter((part): part is string => part !== null),
    highlights: asRows(row.highlights).flatMap((highlight) => {
      const text = str(highlight, 'text');
      return text === null ? [] : [{ day: str(highlight, 'day') ?? '', text }];
    }),
    patterns: strings(row.patterns),
  };
}

/* --- Formatting --------------------------------------------------------- */

const DASH = '—';

/**
 * Markdown is not parsed here. This is another person's text arriving through a
 * link and rendered for a stranger, which is the one page in the app where
 * turning it into HTML would be worth someone's while to attack. But the few
 * markers the model does emit read as typos to anyone who has never seen
 * markdown, so the syntax is stripped and the words kept.
 *
 * Split on every run of newlines rather than on blank lines only: a bullet list
 * separated by single newlines is one string, and HTML collapses those newlines,
 * so the whole list came out as one long run-on line.
 */
export function paragraphs(markdown: string): string[] {
  return markdown
    .replace(/^#{1,6}\s+/gm, '')
    .replace(/\*\*(.+?)\*\*/g, '$1')
    .replace(/^\s*[-*]\s+/gm, '• ')
    .split(/\n+/)
    .map((part) => part.trim())
    .filter((part) => part.length > 0);
}

/**
 * Day strings are plain calendar dates, and they are built from their parts
 * rather than handed to `new Date`. Parsed as a timestamp, "2026-06-03" is
 * midnight UTC, which in any browser west of Greenwich renders as the 2nd —
 * quietly shifting every row in every table by a day.
 */
export function dayCell(day: string | null): string {
  if (day === null) return DASH;
  const [year, month, date] = day.split('-').map(Number);
  if (year === undefined || month === undefined || date === undefined) return day;
  if (Number.isNaN(year) || Number.isNaN(month) || Number.isNaN(date)) return day;
  return new Date(Date.UTC(year, month - 1, date)).toLocaleDateString(undefined, {
    day: 'numeric',
    month: 'short',
    // Spelled out because a shared record is read weeks later, in a folder
    // alongside other people's.
    year: 'numeric',
    timeZone: 'UTC',
  });
}

/**
 * The time is rendered in the OWNER's timezone, not the reader's.
 *
 * `day` is pinned to UTC above so calendar dates cannot drift, but the clock
 * time next to it used to come out in whoever was looking. A check-in at 23:30
 * in London then read as "3 Jun 2026, 6:30 PM" to a therapist in New York —
 * same row, two timezones, and the late-night entry rendered as an early
 * evening one. For a mood record, when in the person's own day something
 * happened is the clinical content, so it has to survive the trip.
 */
function whenCell(day: string | null, at: string | null, timeZone: string): string {
  if (at === null) return dayCell(day);
  // Filed under one day, made on another — the late-night grace, or a check-in
  // inheriting its entry's day. Say both, the owner app's way, rather than
  // printing the filed day with the other day's clock time: that produced
  // "Sep 8, 1:05 AM" for something made at 1:05 on Sep 9.
  if (day !== null && writtenLate(day, at, timeZone)) {
    return `${dayCell(day)} · written ${whenWritten(at, timeZone)}`;
  }
  const time = new Date(at).toLocaleTimeString(undefined, {
    hour: 'numeric',
    minute: '2-digit',
    timeZone,
  });
  return `${dayCell(day)}, ${time}`;
}

/**
 * Whose clock a time is told in.
 *
 * Every date on this page is deliberately the owner's — `whenCell` carries a
 * comment about it — because a day here is a day of his life, not of the
 * reader's. These two sentences in the header were the exception, rendered in
 * whatever zone the browser happened to be in, so a clinician five hours away
 * could read "Put together 3 September" above a page whose newest day was
 * 2 September.
 *
 * The expiry is the one time on this page that genuinely belongs to the reader
 * — it is about when THEIR link stops working — so it keeps their zone, and
 * says so.
 */
/**
 * A date and time with its zone named.
 *
 * The header gives "put together" in the owner's zone and the banner gives the
 * expiry in the reader's, and neither said so — a reader nine hours away saw two
 * times that looked like one clock.
 */
function formatStamp(iso: string, timeZone?: string): string {
  return new Date(iso).toLocaleString(undefined, {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    timeZoneName: 'short',
    ...(timeZone ? { timeZone } : {}),
  });
}

function plain(value: number | null, digits = 0): string {
  return value === null ? DASH : value.toFixed(digits);
}

function signed(value: number | null, digits = 0): string {
  if (value === null) return DASH;
  return value > 0 ? `+${value.toFixed(digits)}` : value.toFixed(digits);
}

/** A before-and-after pair, showing whichever half was recorded. */
function shift(before: number | null, after: number | null): string {
  if (before === null && after === null) return DASH;
  if (after === null) return plain(before);
  if (before === null) return `${after} afterwards`;
  return `${before} → ${after}`;
}

function ofCell(taken: number | null, scheduled: number | null): string {
  if (scheduled === null || scheduled === 0) return DASH;
  return `${taken ?? 0} of ${scheduled}`;
}

function doseCell(amount: number | null, unit: string | null): string {
  if (amount === null) return DASH;
  return unit === null ? String(amount) : `${amount} ${unit}`;
}

function countOf(value: number | null, singular: string, plural: string): string | null {
  if (value === null) return null;
  return `${value} ${value === 1 ? singular : plural}`;
}

/**
 * The SOS paths are stored as keys, and the wording lives in the server's tool
 * table — which this page cannot reach, because it renders with no session.
 * Anything unrecognised still reads as words rather than as raw snake_case.
 */
const SOS_PATHS: Record<string, string> = {
  ground: 'Grounding',
  thought_record: 'A thought record',
  move: 'Moved around',
  talk: 'Talked it through',
  crisis_line: 'Crisis line',
  just_wait: 'Waited it out',
};

export function sosPathName(key: string | null): string {
  if (key === null) return DASH;
  const known = SOS_PATHS[key];
  if (known) return known;
  const words = key.replace(/_/g, ' ').trim();
  return words.charAt(0).toUpperCase() + words.slice(1);
}

/**
 * The adherence rate, above the day-by-day table that produces it.
 *
 * A percentage with no unit attached is the kind of number people carry away
 * wrong, so it says what it is a percentage OF, over what, and how much of it
 * the app merely assumed — in the same breath, not in a footnote. A clinician
 * shown 100% is entitled to know whether that is thirty confirmed days or one
 * inferred afternoon.
 *
 * Stated in days covered rather than "the last 30 days": this table only holds
 * days a dose was scheduled, and claiming a month when it holds three would be
 * the page flattering itself.
 */
function AdherenceHeadline({
  rows,
  ownerToday,
}: {
  rows: Record<string, unknown>[];
  /** The owner's own current day, so the partial one can be left out. */
  ownerToday: string | null;
}): JSX.Element | null {
  // Today is partial by definition — an evening dose is in the denominator
  // hours before it could be in the numerator — so a rate that included it
  // would drop overnight and recover by bedtime. The day-by-day table below
  // still shows it; only the headline figure leaves it out.
  const counted = rows.filter((r) => str(r, 'day') !== ownerToday);

  // Only today so far. Say that, rather than rendering nothing: a headline that
  // silently disappears is indistinguishable from one that failed, and the
  // reader is left to total the table themselves without knowing whether they
  // were meant to. This is the ordinary state of a newly shared record.
  if (counted.length === 0) {
    return (
      <p className="sv-headline">
        <strong>No completed days yet</strong>
        <span>
          A day is only counted once it is over, and today is still going. The table below shows
          it as it stands.
        </span>
      </p>
    );
  }
  const scheduled = counted.reduce((n, r) => n + (num(r, 'meds_scheduled') ?? 0), 0);
  const taken = counted.reduce((n, r) => n + (num(r, 'meds_taken') ?? 0), 0);
  const assumed = counted.reduce((n, r) => n + (num(r, 'meds_assumed') ?? 0), 0);
  // "7 not" is the number a reader acts on, and it is two different things.
  const missed = counted.reduce((n, r) => n + (num(r, 'meds_missed') ?? 0), 0);
  const skipped = counted.reduce((n, r) => n + (num(r, 'meds_skipped') ?? 0), 0);
  const notBreakdown =
    missed + skipped > 0
      ? ` (${[missed > 0 ? `${missed} missed` : null, skipped > 0 ? `${skipped} skipped on purpose` : null]
          .filter(Boolean)
          .join(', ')})`
      : '';
  if (scheduled === 0) return null;
  // Clamped. This is the clinician's copy, and it is where "125% taken · 5
  // taken, -1 not, of 4 scheduled" was actually seen.
  const rate = adherencePercent(taken, scheduled);
  if (rate === null) return null;

  // The other side of the ratio, stated rather than left to be worked out. A
  // reader given only "83% taken" has to do the subtraction to learn that six
  // doses went unaccounted for, and the missing doses are usually the reason
  // they are looking at this table at all.
  const notTaken = Math.max(0, scheduled - taken);
  const takenShown = Math.min(taken, scheduled);

  // Oldest and newest of the rows that were actually counted. The server sends
  // up to 90 days, so no fixed number belongs in this sentence.
  const days = counted.map((r) => str(r, 'day')).filter((d): d is string => d !== null).sort();
  const span =
    days.length > 1 ? `${longDay(days[0]!)} and ${longDay(days[days.length - 1]!)}` : null;

  return (
    <p className="sv-headline">
      <strong>
        {rate}% taken{notTaken > 0 && ` · ${100 - rate}% not`}
      </strong>
      <span>
        {takenShown} taken, {notTaken} not{notBreakdown}, of {scheduled} scheduled — over {counted.length} day
        {counted.length === 1 ? '' : 's'}
        {/* The window the rows actually cover, not a literal. The server sends up
            to 90 days of dose history and this said "in the last 30", so a
            clinician read "over 51 days in the last 30" — a sentence that cannot
            be true, on the figure they are most likely to act on. */}
        {span ? ` between ${span}` : ''}
        {assumed > 0 && ` · ${Math.min(assumed, takenShown)} of those counted without being confirmed`}
        {/*
          The partial day, said in both branches rather than one.
          
          Today is excluded from the rate on purpose — an evening dose sits in
          the denominator hours before it can be in the numerator. The sentence
          explaining that to the reader existed only in the no-completed-days
          branch, so as soon as there was one finished day the explanation
          vanished while today's half-finished row stayed in the table beneath.
          A clinician totting up the rows then gets a different number from the
          headline, with nothing saying why.
        */}
        {rows.some((r) => str(r, 'day') === ownerToday) &&
          ' · today is still going and is not in this figure, though it is in the table below'}
      </span>
    </p>
  );
}

/**
 * The records, as a list that stops.
 *
 * Sixty-nine collapsed rows still ran to six thousand pixels on a phone — better
 * than sixty-nine worksheets, and still further than anyone scrolls. The recent
 * ones are what a clinician opens this for, so those are shown and the rest wait
 * behind an honest count.
 */
function RecordIndex({
  records,
  timezone,
}: {
  records: SheetRecord[];
  timezone: string | null;
}): JSX.Element {
  const LIMIT = 8;
  const [all, setAll] = useState(false);
  const hidden = Math.max(0, records.length - LIMIT);
  const shown = all || hidden === 0 ? records : records.slice(0, LIMIT);

  return (
    <div className="sv-records stack-sm stack">
      <p className="faint">
        {records.length} record{records.length === 1 ? '' : 's'}. Open one to see the full
        worksheet.
      </p>
      {shown.map((record, i) => (
        // The payload deliberately carries no record id — it is one of the
        // handles that would make a leaked copy correlatable — so the key is
        // built from what is left. This list is never reordered.
        <SharedRecord key={`${record.occurredAt}-${i}`} record={record} timezone={timezone} />
      ))}
      {hidden > 0 && !all && (
        <button className="btn btn-ghost btn-block btn-compact" onClick={() => setAll(true)}>
          Show {hidden} older {hidden === 1 ? 'record' : 'records'}
        </button>
      )}
    </div>
  );
}

/**
 * One shared record: a line that opens into the full worksheet.
 *
 * The sheet is built only once it has been opened, and that is the point rather
 * than an optimisation. Collapsing sixty-nine records still left sixty-nine
 * full-page worksheets in the document — every rule, every SVG, every step —
 * hidden but constructed, which on the phone a partner is likely reading this on
 * is the same weight the collapse was meant to remove.
 *
 * Kept mounted once opened, so closing and reopening does not rebuild it and
 * anything the reader had scrolled to stays where they left it.
 */
function SharedRecord({
  record,
  timezone,
}: {
  record: SheetRecord;
  timezone: string | null;
}): JSX.Element {
  const [opened, setOpened] = useState(false);
  const shift =
    record.beliefBefore !== null && record.beliefAfter !== null
      ? `${record.beliefBefore} → ${record.beliefAfter}`
      : null;

  return (
    <details className="sv-record" onToggle={(e) => e.currentTarget.open && setOpened(true)}>
      <summary>
        <span className="sv-record-when">{dayCell(record.occurredAt)}</span>
        <span className="sv-record-thought">{record.automaticThought || 'No thought written'}</span>
        <span className="sv-record-meta">
          {/* Both, where there is a shift. A record can be re-rated and still
              have no fairer version written — half of Dev's unfinished records
              showed here as "93 → 42", indistinguishable from finished work, on
              the index a clinician scans to decide which ones to open. The
              belief moved; the record did not get done. */}
          {shift ? `${shift}${record.isComplete ? '' : ' · not finished'}` : (record.isComplete ? 'finished' : 'not finished')}
        </span>
      </summary>
      {opened && (
        <div className="doc-stage">
          <RecordSheet record={record} timezone={timezone} />
        </div>
      )}
    </details>
  );
}

/**
 * How the person is today.
 *
 * The figures come first and are always here. They are counted from the record
 * and owe nothing to a model, so they do not disappear because the owner chose
 * not to have their journal read — that setting governs one sentence, and an
 * earlier version let it take the mood, the check-in times and the Help now
 * count with it.
 *
 * When there is no written read, the reason is stated. "Nothing was written" and
 * "nothing happened" look identical in a blank space and mean opposite things,
 * and the person reading this is worried enough to fill that silence in for
 * themselves if nobody else does.
 */
function DailyStatus({
  row,
  lineLabel,
}: {
  row: Record<string, unknown> | null;
  /** Names the kind of line, at What my days held and above. */
  lineLabel?: (kind: DayLineKind) => string;
}): JSX.Element {
  if (!row) {
    return (
      <Section title="Today">
        <Nothing>
          <p>Today could not be read. Try reloading.</p>
        </Nothing>
      </Section>
    );
  }

  const analysis = str(row, 'analysis') ?? 'quiet';
  const state = str(row, 'state');
  const headline = str(row, 'headline');
  const kind = str(row, 'lineKind');
  const kindLabel = lineLabel && (kind === 'held' || kind === 'seemed') ? lineLabel(kind) : null;
  const themes = strings(row.themes);
  const stats: Record<string, unknown> = isRow(row.stats) ? row.stats : {};
  const trend = str(row, 'trend');

  const mood = num(stats, 'meanMood');
  const lo = num(stats, 'lowestMood');
  const hi = num(stats, 'highestMood');
  const checkins = num(stats, 'checkins') ?? 0;
  const sos = num(stats, 'sosEvents') ?? 0;
  const relief = num(stats, 'sosRelief');
  const started = num(stats, 'thoughtRecords') ?? 0;
  const finished = num(stats, 'thoughtRecordsCompleted') ?? 0;
  const entries = num(stats, 'journalEntries') ?? 0;
  const medsTaken = num(stats, 'medsTaken') ?? 0;
  const medsScheduled = num(stats, 'medsScheduled') ?? 0;
  const medsAssumed = num(stats, 'medsAssumed') ?? 0;
  const lastAt = str(stats, 'lastCheckinAt');
  const overwhelm = num(stats, 'meanOverwhelm');

  /*
   * Withheld and empty are not the same thing, and the page was saying so wrongly.
   *
   * Figures the owner did not share arrive as null; figures for a day nothing
   * happened on arrive as 0. Every reader above defaults null to 0, which
   * collapses the two — so a Partner link, which withholds mood and Help now on
   * purpose, rendered "Nothing logged yet today" for somebody who had used the
   * crisis screen twice that morning. That is not a privacy boundary, it is the
   * app telling a partner something untrue on the owner's behalf.
   */
  /*
   * ANY of them, not all of them.
   *
   * This was an AND across the five, which is true only of the Partner preset —
   * the one scope set that withholds every figure at once. Under the Therapist
   * preset exactly one came back null, `journalEntries`, so for somebody whose
   * only activity that day was writing, every figure read 0, `facts` came out
   * empty, and the page told a clinician "Nothing logged yet today" about a day
   * he had written twice in. (Since 10 Sep the journal count travels on every
   * link; an older server can still send it null, so it stays in the list.)
   *
   * The fix above described the right rule and implemented the narrowest case
   * of it. If anything at all was withheld and there is nothing left to show,
   * the honest sentence is that the detail was not shared — not that there was
   * none.
   */
  /*
   * Nothing happened, or nothing was shown? The server now answers it.
   *
   * This inferred the difference from whether any figure came back null, and
   * one of the five it consulted was `medsScheduled` — which is null on EVERY
   * day of a link without the Medications add-on. So the condition was
   * permanently true on his therapist's link, and every quiet day told her he
   * "hasn't shared the detail of today" about a day he had shared in full.
   *
   * Deleting that one clause was the obvious fix and it is wrong in the other
   * direction: `medsScheduled` is also null when nothing was scheduled, so a
   * day he took his meds on would then claim nothing was logged. The fix is
   * not a better guess, it is to stop guessing — `empty` is counted from the
   * record server-side and is never withheld, which is exactly what makes the
   * day fold below correct about the same question.
   */
  const empty = row.empty === true;
  const withheld = !empty;

  /* Facts, each stated only when there is one. A row reading "0" for everything
     is not information about a person, it is information about whether they
     opened the app, and presenting the two identically is how a quiet Tuesday
     comes to look like a worrying one. */
  const facts: { label: string; value: string }[] = [];
  if (checkins > 0) {
    facts.push({
      label: `Mood${lastAt ? `, last at ${lastAt}` : ''}`,
      value:
        mood === null
          ? `${checkins} check-in${checkins === 1 ? '' : 's'}`
          : /* The word first, then the figure it describes. `moodLabel` bands a
               mean correctly, so this is right while the figure is still an
               average; making it the day-end rating is the next release. */
            `${moodLabel(mood)} · ${signed(mood, 1)} average` +
            (lo !== null && hi !== null && lo !== hi ? ` · ${signed(lo)} to ${signed(hi)}` : '') +
            ` · ${checkins} check-in${checkins === 1 ? '' : 's'}`,
    });
  }
  if (overwhelm !== null) facts.push({ label: 'Overwhelm', value: `${overwhelm} out of 5 on average` });
  if (sos > 0) {
    facts.push({
      label: 'Help now',
      value:
        `${sos} time${sos === 1 ? '' : 's'}` +
        (relief !== null && relief > 0 ? ` · distress down ${relief} on average after` : ''),
    });
  }
  if (started > 0) {
    facts.push({
      label: 'Thought records',
      value: `${started} started${finished > 0 ? `, ${finished} worked through` : ''}`,
    });
  }
  if (entries > 0) facts.push({ label: 'Journal', value: `${entries} entr${entries === 1 ? 'y' : 'ies'}` });
  if (medsScheduled > 0) {
    facts.push({ label: 'Medication', value: doseFact(medsTaken, medsScheduled, medsAssumed) });
  }

  const stateWord =
    state === 'struggling' ? 'Having a hard day' : state === 'mixed' ? 'Up and down' : 'Steady';

  return (
    <Section title="Today">
      <div id={TODAY_BLOCK} className={`sv-status sv-status-${state ?? 'plain'}`}>
        {analysis === 'ready' && (
          <>
            <strong className="sv-status-state">{stateWord}</strong>
            {headline && kindLabel && <span className="sv-line-kind">{kindLabel}</span>}
            {headline && <p className="sv-status-line">{headline}</p>}
          </>
        )}

        {facts.length > 0 ? (
          <dl className="sv-facts">
            {facts.map((f) => (
              <div key={f.label}>
                <dt>{f.label}</dt>
                <dd>{f.value}</dd>
              </div>
            ))}
          </dl>
        ) : withheld ? (
          <p className="muted">
            Something was logged today, but not shared on this link.
          </p>
        ) : (
          <p className="muted">Nothing logged yet today.</p>
        )}

        {trend && checkins > 0 && (
          <p className="faint">
            {trend === 'worse' && 'Lower than their own average for the last two weeks.'}
            {trend === 'better' && 'Higher than their own average for the last two weeks.'}
            {trend === 'similar' && 'About the same as their own average for the last two weeks.'}
          </p>
        )}

        {themes.length > 0 && (
          <div className="chips" style={{ marginTop: 'var(--space-2)' }}>
            {themes.map((t) => (
              <span className="chip" key={t} style={{ cursor: 'default' }}>
                {t}
              </span>
            ))}
          </div>
        )}

        {/* Why there is no sentence. Never phrased as an instruction to the
            reader, who cannot change any of it — only the owner can, and they
            are told the actionable version on their own share screen. */}
        {analysis === 'disabled' && (
          <p className="faint sv-status-note">
            A written summary of today can be produced automatically, but the AI features that write it
            are switched off on this account. The figures above are current either way.
          </p>
        )}
        {analysis === 'unavailable' && (
          <p className="faint sv-status-note">
            No written summary today — the writing did not come back this time. The figures
            above are current either way.
          </p>
        )}
        {/* NOT WRITTEN YET, which is a different thing from nothing to say and
            must not be rendered as it. This page no longer commissions the
            writing — opening a link must never spend somebody else's money or
            start AI activity on their account — so it is written on a schedule
            instead, and this is the gap between the day moving and the next
            sweep. Without a note here the reader sees figures and no sentence,
            which reads as a day with nothing in it. */}
        {analysis === 'pending' && (
          <p className="faint sv-status-note">
            Today&rsquo;s written summary is not ready — either none has been made yet, or the day
            has moved on since the last one. The app writes it on its own schedule, usually within
            about ten minutes, though overnight it can wait until morning — never because this page
            was opened. The figures above are current.
          </p>
        )}
      </div>
    </Section>
  );
}

/**
 * The week, with one day of it open.
 *
 * The strip on its own answered "has this been going on" and nothing else. A
 * reader who could see that Monday was hard had no way to ask how — the block
 * above gives today a state, a sentence, a range, a trend and a set of tags,
 * and the moment today became yesterday all of that collapsed into five counts.
 * The day somebody actually had is not less worth reading on Thursday than it
 * was on Monday.
 *
 * Tapping the open day again closes it. A fold with no way out is a trap on a
 * page with no navigation, and this is the one screen in the app whose reader
 * cannot go anywhere else.
 */
/** The one element on this page anything else needs to point at. */
const TODAY_BLOCK = 'sv-today';

/**
 * Take the reader to the block for today rather than building a second one.
 *
 * Flashed as well as scrolled, and the flash is the part that matters: a page
 * that silently jumps has not told you it moved, and on a long record a reader
 * who loses their place has to find it again by reading. Motion is skipped
 * where the reader has asked for less of it — the highlight still runs, so the
 * answer is never only an animation.
 */
function showToday(): void {
  const el = document.getElementById(TODAY_BLOCK);
  if (!el) return;
  const still = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false;

  el.scrollIntoView({ behavior: still ? 'auto' : 'smooth', block: 'center' });

  /*
   * And then check that it actually went, because `behavior: 'smooth'` is not
   * honoured everywhere and where it is ignored it does not fall back — the
   * call returns having done nothing at all. Measured here: smooth moved the
   * page zero pixels in a browser where the same call with `auto` moved 1168.
   *
   * The failure that guards against is the worst kind on this page. The reader
   * presses today, nothing happens, and there is no second thing to try and
   * nobody to ask — a clinician gets one visit and no way to tell a dead
   * control from a page that has finished loading.
   */
  if (!still) {
    window.setTimeout(() => {
      const r = el.getBoundingClientRect();
      const arrived = r.top < window.innerHeight && r.bottom > 0;
      if (!arrived) el.scrollIntoView({ behavior: 'auto', block: 'center' });
    }, 700);
  }

  el.classList.remove('sv-flash');
  // Forced reflow: without it the class is removed and re-added inside one
  // frame and the animation never restarts, so pressing today twice does
  // nothing the second time.
  void el.offsetWidth;
  el.classList.add('sv-flash');
  window.setTimeout(() => el.classList.remove('sv-flash'), 1400);
}

function WeekBehind({
  days,
  picked,
  onPick,
  alreadyShown,
  today,
  lineLabel,
}: {
  days: DayGlance[];
  picked: string | null;
  onPick: (day: string | null) => void;
  /** The day the section above already shows in full. Never opened down here. */
  alreadyShown: string | null;
  /** The owner's today, so the strip does not label days in the reader's clock. */
  today: string | null;
  lineLabel?: (kind: DayLineKind) => string;
}): JSX.Element {
  const open = days.find((d) => d.day === picked && d.day !== alreadyShown) ?? null;
  return (
    <div className="stack-sm stack">
      <DayStrip
        days={days}
        picked={picked}
        today={today}
        lineLabel={lineLabel}
        onPick={(day) => {
          // Today is answered in full above. Pressing its card goes there
          // instead of opening a duplicate; the fold is for the days that have
          // nowhere else on this page to be read.
          if (day === alreadyShown) {
            onPick(null);
            showToday();
            return;
          }
          onPick(day === picked ? null : day);
        }}
        alreadyShown={alreadyShown}
      />
      {/* Said once, under the row, because a card that opens looks exactly like
          a card that does not and this page has no second visit to learn on. */}
      <p className="faint day-strip-hint">Open a day for more on how it went.</p>
      {open && <PickedDay glance={open} onClose={() => onPick(null)} lineLabel={lineLabel} />}
    </div>
  );
}

/**
 * One past day, opened.
 *
 * Built to the same rule as the block for today: a fact appears only where
 * there is one, because a fold reading zero across the board describes whether
 * somebody opened an app rather than how they were, and presenting the two
 * alike is how a quiet Tuesday comes to look like a worrying one.
 *
 * The sentence is the part that may be missing, and its absence is explained
 * rather than left blank. Most past days have never been read — a day nobody
 * was well enough to open the app on is exactly the day with nothing cached —
 * and a gap where a line might have been reads as "nothing to say about him".
 */
function PickedDay({
  glance,
  onClose,
  lineLabel,
}: {
  glance: DayGlance;
  onClose: () => void;
  lineLabel?: (kind: DayLineKind) => string;
}): JSX.Element {
  const d = glance.detail;
  const kindLabel = lineLabel && glance.lineKind ? lineLabel(glance.lineKind) : null;
  const logged =
    glance.checkins + glance.sosEvents + glance.thoughtRecords + glance.journalEntries;

  const facts: { label: string; value: string }[] = [];
  if (glance.checkins > 0) {
    const range =
      d && d.lowestMood !== null && d.highestMood !== null && d.lowestMood !== d.highestMood
        ? ` · ${signed(d.lowestMood)} to ${signed(d.highestMood)}`
        : '';
    facts.push({
      label: `Mood${d?.lastCheckinAt ? `, last at ${d.lastCheckinAt}` : ''}`,
      value:
        (glance.meanMood === null
          ? ''
          : `${moodLabel(glance.meanMood)} · ${signed(glance.meanMood, 1)} average${range} · `) +
        `${glance.checkins} check-in${glance.checkins === 1 ? '' : 's'}`,
    });
  }
  if (d?.meanOverwhelm != null) {
    facts.push({ label: 'Overwhelm', value: `${d.meanOverwhelm} out of 5 on average` });
  }
  if (glance.sosEvents > 0) {
    facts.push({
      label: 'Help now',
      value:
        `${glance.sosEvents} time${glance.sosEvents === 1 ? '' : 's'}` +
        (d?.sosRelief != null && d.sosRelief > 0
          ? ` · distress down ${d.sosRelief} on average after`
          : ''),
    });
  }
  if (glance.thoughtRecords > 0) {
    facts.push({
      label: 'Thought records',
      value:
        `${glance.thoughtRecords} started` +
        (d && d.thoughtRecordsCompleted > 0 ? `, ${d.thoughtRecordsCompleted} worked through` : ''),
    });
  }
  if (glance.journalEntries > 0) {
    facts.push({
      label: 'Journal',
      value: `${glance.journalEntries} entr${glance.journalEntries === 1 ? 'y' : 'ies'}`,
    });
  }
  if (glance.medsScheduled > 0) {
    facts.push({
      label: 'Medication',
      value: doseFact(glance.medsTaken, glance.medsScheduled, glance.medsAssumed),
    });
  }

  const stateWord =
    glance.state === 'struggling'
      ? 'A hard day'
      : glance.state === 'mixed'
        ? 'Up and down'
        : glance.state === 'steady'
          ? 'Steady'
          : null;

  return (
    <div className={`sv-status sv-picked sv-status-${glance.state ?? 'plain'}`}>
      <div className="sv-picked-head">
        <strong>{longDay(glance.day)}</strong>
        <button type="button" className="btn btn-ghost btn-compact" onClick={onClose}>
          Close
        </button>
      </div>

      {stateWord && <strong className="sv-status-state">{stateWord}</strong>}
      {glance.headline && kindLabel && <span className="sv-line-kind">{kindLabel}</span>}
      {glance.headline && <p className="sv-status-line">{glance.headline}</p>}

      {facts.length > 0 ? (
        <dl className="sv-facts">
          {facts.map((f) => (
            <div key={f.label}>
              <dt>{f.label}</dt>
              <dd>{f.value}</dd>
            </div>
          ))}
        </dl>
      ) : glance.empty ? (
        <p className="muted">Nothing was logged on this day.</p>
      ) : (
        /* Something was logged; this reader was not given the detail of it.
           `empty` is counted from the record and is never withheld, which is
           what makes it the honest signal for the difference. */
        <p className="muted">Something was logged on this day, but not shared.</p>
      )}

      {d?.trend && glance.checkins > 0 && (
        <p className="faint">
          {d.trend === 'worse' && 'Lower than their own average for the fortnight before it.'}
          {d.trend === 'better' && 'Higher than their own average for the fortnight before it.'}
          {d.trend === 'similar' && 'About the same as their own average for the fortnight before it.'}
        </p>
      )}

      {d && d.themes.length > 0 && (
        <div className="chips" style={{ marginTop: 'var(--space-2)' }}>
          {d.themes.map((t) => (
            <span className="chip" key={t} style={{ cursor: 'default' }}>
              {t}
            </span>
          ))}
        </div>
      )}

      {/* Only where the figures show a day worth a sentence. Saying it on a day
          with nothing in it would explain an absence nobody was wondering
          about. */}
      {!glance.headline && logged > 0 && (
        <p className="faint sv-status-note">
          No written summary of this day here — none was written at the time, or the one that was is not
          included on this link. The figures above are counted from the record either way.
        </p>
      )}
    </div>
  );
}

/** "Monday 25 August". Parsed at local midday — a bare date is UTC and lands
    on the previous evening west of Greenwich. */
export function longDay(day: string): string {
  const [y, m, dd] = day.split('-').map(Number);
  if (!y || !m || !dd) return day;
  return new Date(y, m - 1, dd, 12).toLocaleDateString(undefined, {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
  });
}

/** `YYYY-MM-DD` of an instant in a given timezone — the owner's day, not the reader's. */
export function dayIn(iso: string, timezone: string): string | null {
  try {
    return new Intl.DateTimeFormat('en-CA', {
      timeZone: timezone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    }).format(new Date(iso));
  } catch {
    return null;
  }
}

/**
 * One day's doses, saying which of them nobody confirmed.
 *
 * The strip card said "1/1 doses, assumed" and the Today block and the fold said
 * "1 of 1 doses accounted for" about the same doses — so the same page told a
 * clinician a default and, a few lines lower, something that read like a
 * confirmation. The wording after the dot is the dose summary's own, above.
 */
function doseFact(taken: number, scheduled: number, assumed: number): string {
  const base = `${taken} of ${scheduled} doses accounted for`;
  return assumed > 0 ? `${base} · ${Math.min(assumed, taken)} of those counted without being confirmed` : base;
}
